import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Settings, Save, RotateCcw, ShieldCheck, AlertTriangle, Clock, ChevronRight, RefreshCw, Check } from 'lucide-react';

const POLICY_LABELS = {
  patient_responsibility: { name: 'Patient Responsibility', desc: 'Controls when a balance becomes patient responsibility' },
  statement_workflow: { name: 'Statement Workflow', desc: 'Controls statement readiness and generation rules' },
  dispatch_cadence: { name: 'Dispatch Cadence', desc: 'Controls statement dispatch timing and batching' },
  collections: { name: 'Collections', desc: 'Controls when cases enter collections review' },
  collections_cadence: { name: 'Collections Cadence', desc: 'Controls collections follow-up timing and batching' },
  writeoff: { name: 'Write-Off', desc: 'Controls when cases enter write-off review' },
};

const FIELD_LABELS = {
  wait_for_secondary: 'Wait for Secondary Adjudication',
  suppress_if_appeal: 'Suppress if Appeal Active',
  minimum_statement_threshold_cents: 'Minimum Statement Threshold (cents)',
  small_balance_accumulation_threshold_cents: 'Small Balance Accumulation Threshold (cents)',
  small_balance_accumulation_days: 'Small Balance Accumulation Days',
  front_desk_collection_prompt_threshold_cents: 'Front Desk Collection Prompt Threshold (cents)',
  payment_plan_offer_threshold_cents: 'Payment Plan Offer Threshold (cents)',
  collections_enabled: 'Collections Enabled',
  collections_requires_manual_approval: 'Collections Requires Manual Approval',
  small_balance_accumulation_enabled: 'Small Balance Accumulation Enabled',
  suppress_statement_if_dispute_active: 'Suppress Statement if Dispute Active',
  suppress_statement_if_manual_hold_active: 'Suppress Statement if Manual Hold',
  suppress_statement_if_hardship_flag: 'Suppress Statement if Hardship',
  days_after_secondary_before_statement: 'Days After Secondary Before Statement',
  days_after_recent_payment_before_statement: 'Days After Recent Payment',
  require_manual_approval_before_first_statement: 'Require Manual Approval Before First Statement',
  allow_statement_when_payment_plan_active: 'Allow Statement with Active Payment Plan',
  minimum_days_between_statements: 'Minimum Days Between Statements',
  max_statements_per_case_per_cycle_window: 'Max Statements Per Cycle Window',
  batch_default_channel: 'Default Batch Channel',
  batch_max_items: 'Batch Max Items',
  exclude_if_recent_dispatch_failed_within_days: 'Exclude if Recent Dispatch Failed (days)',
  exclude_if_recent_payment_within_days: 'Exclude if Recent Payment (days)',
  exclude_if_recent_statement_sent_within_days: 'Exclude if Recent Statement Sent (days)',
  require_batch_approval_before_run: 'Require Batch Approval Before Run',
  allow_same_day_regen_and_send: 'Allow Same-Day Regen and Send',
  days_after_last_statement_before_collections: 'Days After Last Statement Before Collections',
  minimum_balance_for_collections_cents: 'Minimum Balance for Collections (cents)',
  max_statements_before_collections: 'Max Statements Before Collections',
  block_if_payment_plan_active: 'Block if Payment Plan Active',
  block_if_recent_payment_within_days: 'Block if Recent Payment (days)',
  block_if_dispute_active: 'Block if Dispute Active',
  block_if_hardship_flag: 'Block if Hardship Flag',
  require_manual_approval_for_collections: 'Require Manual Approval',
  minimum_days_between_collections_followups: 'Min Days Between Follow-ups',
  max_followups_per_cycle_window: 'Max Follow-ups Per Cycle',
  exclude_if_recent_patient_contact_within_days: 'Exclude if Recent Contact (days)',
  require_assignment_before_batch_run: 'Require Assignment Before Run',
  minimum_days_after_last_collections_activity_before_writeoff: 'Min Days After Collections Before Write-Off',
  minimum_balance_for_writeoff_review_cents: 'Min Balance for Write-Off (cents)',
  allow_writeoff_review_after_failed_collections: 'Allow After Failed Collections',
  allow_writeoff_review_after_internal_close: 'Allow After Internal Close',
  allow_writeoff_review_after_agency_close: 'Allow After Agency Close',
  require_manual_approval_for_writeoff_execution: 'Require Manual Approval for Write-Off',
};

function PolicyField({ name, value, onChange }) {
  const label = FIELD_LABELS[name] || name.replace(/_/g, ' ');
  if (typeof value === 'boolean') {
    return (
      <div className="flex items-center justify-between py-2 border-b last:border-0">
        <label className="text-xs font-medium">{label}</label>
        <Switch checked={value} onCheckedChange={v => onChange(name, v)} data-testid={`field-${name}`} />
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-0 gap-4">
      <label className="text-xs font-medium shrink-0">{label}</label>
      <Input type={typeof value === 'number' ? 'number' : 'text'} value={value ?? ''} onChange={e => onChange(name, typeof value === 'number' ? Number(e.target.value) : e.target.value)} className="max-w-48 h-7 text-xs" data-testid={`field-${name}`} />
    </div>
  );
}

function ValidationPanel({ result }) {
  if (!result) return null;
  const { valid, errors, warnings } = result;
  if (valid && !warnings?.length) return <div className="flex items-center gap-1.5 text-xs text-emerald-600 py-2"><Check className="h-3.5 w-3.5" />Configuration is valid.</div>;
  return (
    <div className="space-y-2 py-2" data-testid="validation-panel">
      {errors?.map((e, i) => <div key={i} className="flex items-start gap-1.5 text-xs text-red-600"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />{e}</div>)}
      {warnings?.map((w, i) => <div key={i} className="flex items-start gap-1.5 text-xs text-amber-600"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />{w}</div>)}
    </div>
  );
}

// === POLICY DETAIL ===
function PolicyDetail({ policyType, onBack }) {
  const [values, setValues] = useState(null);
  const [original, setOriginal] = useState(null);
  const [versions, setVersions] = useState([]);
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rollbackDialog, setRollbackDialog] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, vRes] = await Promise.all([
        api.get(`/revenue/policies/${policyType}`),
        api.get(`/revenue/policies/${policyType}/versions`),
      ]);
      setValues(pRes.data.policy);
      setOriginal(pRes.data.policy);
      setVersions(vRes.data.versions || []);
    } catch { toast.error('Failed to load policy.'); }
    finally { setLoading(false); }
  }, [policyType]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleChange = (key, val) => setValues(prev => ({ ...prev, [key]: val }));

  const handleValidate = async () => {
    try {
      const res = await api.post('/revenue/policies/validate', { policy_type: policyType, values });
      setValidation(res.data);
      return res.data;
    } catch { toast.error('Validation failed.'); return null; }
  };

  const handleSave = async () => {
    const v = await handleValidate();
    if (!v) return;
    if (v.errors?.length) { toast.error('Fix errors before saving.'); return; }
    if (v.warnings?.length && !window.confirm(`${v.warnings.length} warning(s) found. Save anyway?`)) return;
    setSaving(true);
    try {
      await api.put(`/revenue/policies/${policyType}`, values);
      toast.success('Policy saved.');
      setValidation(null);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const handleRollback = async (version) => {
    try {
      await api.post(`/revenue/policies/${policyType}/rollback`, { target_version: version });
      toast.success(`Rolled back to version ${version}.`);
      setRollbackDialog(null);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rollback failed.'); }
  };

  const meta = POLICY_LABELS[policyType] || { name: policyType, desc: '' };
  const hasChanges = JSON.stringify(values) !== JSON.stringify(original);

  if (loading) return <div className="space-y-2 p-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-64 w-full" /></div>;

  return (
    <div className="space-y-4" data-testid="policy-detail">
      <Button variant="ghost" size="sm" className="gap-1" onClick={onBack}><ArrowLeft className="h-3 w-3" />All Policies</Button>
      <div>
        <h2 className="text-base font-semibold">{meta.name}</h2>
        <p className="text-xs text-muted-foreground">{meta.desc}</p>
      </div>

      {/* Form */}
      <Card className="border">
        <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm">Configuration</CardTitle></CardHeader>
        <CardContent className="p-4">
          {values && Object.entries(values).map(([k, v]) => <PolicyField key={k} name={k} value={v} onChange={handleChange} />)}
        </CardContent>
      </Card>

      {/* Validation */}
      <ValidationPanel result={validation} />

      {/* Actions */}
      <div className="flex gap-2">
        <Button size="sm" className="gap-1 text-xs" onClick={handleValidate} variant="outline" data-testid="validate-btn"><ShieldCheck className="h-3 w-3" />Validate</Button>
        <Button size="sm" className="gap-1 text-xs" onClick={handleSave} disabled={saving || !hasChanges} data-testid="save-btn"><Save className="h-3 w-3" />{saving ? 'Saving...' : 'Save'}</Button>
        {hasChanges && <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setValues(original); setValidation(null); }}>Reset</Button>}
      </div>

      {/* Version History */}
      <Card className="border">
        <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm flex items-center gap-2"><Clock className="h-3.5 w-3.5" />Version History ({versions.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {versions.length === 0 ? <div className="p-4 text-xs text-muted-foreground text-center">No version history yet.</div> : (
            <table className="w-full text-xs">
              <thead><tr className="border-b bg-muted/20">
                <th className="px-3 py-1.5 text-left">Version</th><th className="px-3 py-1.5 text-left">Changed By</th>
                <th className="px-3 py-1.5 text-left">Date</th><th className="px-3 py-1.5 text-left"></th>
              </tr></thead>
              <tbody>{versions.map(v => (
                <tr key={v.id} className="border-b hover:bg-muted/10">
                  <td className="px-3 py-1.5 font-medium">v{v.version}</td>
                  <td className="px-3 py-1.5">{(v.changed_by || '').slice(0, 10)}...</td>
                  <td className="px-3 py-1.5 text-muted-foreground">{new Date(v.changed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                  <td className="px-3 py-1.5"><Button size="sm" variant="ghost" className="h-6 text-[10px] gap-1" onClick={() => setRollbackDialog(v)} data-testid={`rollback-v${v.version}`}><RotateCcw className="h-3 w-3" />Rollback</Button></td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Rollback Dialog */}
      {rollbackDialog && (
        <Dialog open={!!rollbackDialog} onOpenChange={() => setRollbackDialog(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Rollback to v{rollbackDialog.version}?</DialogTitle></DialogHeader>
            <p className="text-xs text-muted-foreground">This creates a new version with the values from v{rollbackDialog.version}. History is preserved.</p>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setRollbackDialog(null)}>Cancel</Button>
              <Button size="sm" onClick={() => handleRollback(rollbackDialog.version)} data-testid="confirm-rollback">Rollback</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// === MAIN PAGE ===
export default function RevenuePolicyAdmin() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedType = searchParams.get('type');
  const [policies, setPolicies] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get('/revenue/policies'); setPolicies(res.data.policies); }
    catch { toast.error('Failed to load policies.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (!selectedType) fetch(); }, [fetch, selectedType]);

  if (selectedType) {
    return (
      <div className="flex-1 overflow-auto p-4 max-w-4xl">
        <PolicyDetail policyType={selectedType} onBack={() => setSearchParams({})} />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4 max-w-4xl" data-testid="policy-admin">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight flex items-center gap-2"><Settings className="h-5 w-5" />Revenue Cycle Policies</h1>
        <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={() => navigate('/revenue')}><ArrowLeft className="h-3 w-3" />Revenue Workspace</Button>
      </div>

      {loading ? <div className="space-y-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(POLICY_LABELS).map(([type, meta]) => (
            <Card key={type} className="border hover:border-primary/30 cursor-pointer transition-colors" onClick={() => setSearchParams({ type })} data-testid={`policy-card-${type}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">{meta.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{meta.desc}</div>
                  {policies?.[type] && <div className="text-[10px] text-muted-foreground mt-1">{Object.keys(policies[type]).length} settings</div>}
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
